import { useParams } from 'react-router-dom';
import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { SalesOrder } from '../../lib/types/crm.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

/** Printable sales order. It deliberately makes no invoice, revenue, tax, or receivable claim. */
export function OrderPrintPage() {
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const { unit, label, ready } = useAmountDisplay();
  const base = slug ? tenantBasePath(slug) : '';
  const order = useApiGet<SalesOrder>(slug && ref ? `${base}/api/v1/sales/orders/${ref}` : null, [
    slug,
    ref,
  ]);
  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties` : null,
    [slug],
  );

  if (!slug || !ref) return null;
  if (!ready) {
    return (
      <PrintPageShell backHref={`${base}/crm/orders/${ref}`} backLabel="بازگشت به سفارش">
        <PrintLoadState />
      </PrintPageShell>
    );
  }

  const backHref = `${base}/crm/orders/${ref}`;

  if (!isReady(order)) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به سفارش">
        <PrintLoadState error={order.status === 'error' ? order.error : undefined} />
      </PrintPageShell>
    );
  }

  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;
  const partyRow =
    isReady(parties) && order.data.partyRef
      ? parties.data.items.find((p) => String(p.code) === order.data.partyRef)
      : undefined;
  const buyerName = partyRow
    ? String(partyRow.name ?? partyRow.code)
    : (order.data.partyRef ?? '—');
  const lines = order.data.lines ?? [];
  const today = systemClock.today();

  return (
    <PrintPageShell backHref={backHref} backLabel="بازگشت به سفارش">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 12.5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <TenantIdentityBlock
            legalIdentity={baseOrg?.legalIdentity ?? null}
            primaryLocation={baseOrg?.primaryLocation ?? null}
            personType={baseOrg?.personType ?? null}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-end',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700 }}>سفارش فروش</span>
            <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
              شماره سفارش {ref}
            </span>
            {order.data.quoteRef ? (
              <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                پیش‌فاکتور مرجع {order.data.quoteRef}
              </span>
            ) : null}
            <span>تاریخ تهیه {displayJalaliDate(today)}</span>
          </div>
        </div>

        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: 6,
            padding: '10px 14px',
            display: 'flex',
            gap: 24,
          }}
        >
          <span style={{ color: '#555' }}>خریدار</span>
          <span style={{ fontWeight: 600 }}>{buyerName}</span>
          {partyRow ? (
            <span
              className="ltr sk-num"
              style={{ color: '#555', fontFamily: 'var(--sk-font-mono)' }}
            >
              {String(partyRow.code)}
            </span>
          ) : null}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333' }}>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>#</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>شرح کالا</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>کد کالا</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>مقدار</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>واحد</th>
              <th style={{ textAlign: 'left', padding: '6px 4px' }}>فی ({label})</th>
              <th style={{ textAlign: 'left', padding: '6px 4px' }}>جمع ردیف ({label})</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '14px 4px', color: '#777' }}>
                  قلمی برای این سفارش ثبت نشده است.
                </td>
              </tr>
            ) : (
              lines.map((line) => (
                <tr key={line.rowNumber} style={{ borderBottom: '1px solid #e2e2e2' }}>
                  <td style={{ padding: '6px 4px' }}>{line.rowNumber.toLocaleString('fa-IR')}</td>
                  <td style={{ padding: '6px 4px' }}>{line.description}</td>
                  <td
                    className="ltr sk-num"
                    style={{ padding: '6px 4px', fontFamily: 'var(--sk-font-mono)' }}
                  >
                    {line.productRef}
                  </td>
                  <td style={{ padding: '6px 4px' }}>{line.quantity}</td>
                  <td style={{ padding: '6px 4px' }}>{line.uomRef}</td>
                  <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                    {formatRialFa(line.unitPriceRial, unit)}
                  </td>
                  <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                    {formatRialFa(line.lineTotalRial, unit)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div
            style={{
              width: 280,
              border: '1px solid #ccc',
              borderRadius: 6,
              padding: '10px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 700,
            }}
          >
            <span>مبلغ سفارش</span>
            <span className="ltr sk-num">
              {formatRialFa(order.data.totalRial, unit)} {label}
            </span>
          </div>
        </div>
      </div>
    </PrintPageShell>
  );
}
